import type { ChainSide } from "./optionsChain.types";

/**
 * Roving-focus coordinates for the chain grid.
 *
 * A chain mounts hundreds of identically shaped quote buttons, so only one is a
 * tab stop and the arrow keys move between them. Kept out of the grid module so
 * that file exports components only, which is what Fast Refresh needs.
 */
export interface FocusCell {
  rowIndex: number;
  side: ChainSide;
  metricIndex: number;
}

/** Address used to re-find a button after virtualization remounts its row. */
export const cellAddress = (cell: FocusCell) =>
  `${cell.rowIndex}:${cell.side}:${cell.metricIndex}`;

const sameCell = (a: FocusCell, b: FocusCell) =>
  a.rowIndex === b.rowIndex &&
  a.side === b.side &&
  a.metricIndex === b.metricIndex;

export interface FocusStepArgs {
  key: string;
  current: FocusCell;
  /** Actionable column indexes on the current side, ascending. */
  actionableIndexes: readonly number[];
  /**
   * Actionable column indexes on the *other* side. Bid and ask are pinned to
   * opposite ends of the two panes, so the two sides do not share indexes and
   * crossing the strike column needs the target side's own list.
   */
  crossSideIndexes: readonly number[];
  rowCount: number;
}

/**
 * One step for an arrow key, or undefined when the key does not move.
 *
 * Sideways movement walks only the columns that render a button, so arrowing
 * right steps bid → ask → the other side's bid instead of stalling on a
 * read-only cell such as Volume.
 */
const stepFocusCell = ({
  key,
  current,
  actionableIndexes,
  crossSideIndexes,
  rowCount,
}: FocusStepArgs): FocusCell | undefined => {
  const lastRow = Math.max(0, rowCount - 1);
  const last = actionableIndexes.length - 1;
  if (last < 0) {
    return undefined;
  }
  // -1 when the current column stopped being actionable, which sends the next
  // sideways step to the first actionable column.
  const position = actionableIndexes.indexOf(current.metricIndex);

  switch (key) {
    case "ArrowDown":
      return { ...current, rowIndex: Math.min(lastRow, current.rowIndex + 1) };
    case "ArrowUp":
      return { ...current, rowIndex: Math.max(0, current.rowIndex - 1) };
    case "ArrowRight":
      if (position < last) {
        return { ...current, metricIndex: actionableIndexes[position + 1] };
      }
      // Off the right edge of the calls pane: continue into the puts pane at its
      // leftmost actionable column.
      return current.side === "call" && crossSideIndexes.length > 0
        ? { ...current, side: "put", metricIndex: crossSideIndexes[0] }
        : undefined;
    case "ArrowLeft":
      if (position > 0) {
        return { ...current, metricIndex: actionableIndexes[position - 1] };
      }
      return current.side === "put" && crossSideIndexes.length > 0
        ? {
            ...current,
            side: "call",
            metricIndex: crossSideIndexes[crossSideIndexes.length - 1],
          }
        : undefined;
    case "Home":
      return { ...current, rowIndex: 0 };
    case "End":
      return { ...current, rowIndex: lastRow };
    default:
      return undefined;
  }
};

/**
 * Next focus cell for an arrow key, skipping past cells with nothing to
 * activate. A deep out-of-the-money strike often has no bid, and stopping on it
 * would look like the arrow key had stopped working.
 */
export const nextFocusCell = ({
  key,
  current,
  actionableIndexes,
  crossSideIndexes,
  rowCount,
  canFocus = () => true,
}: FocusStepArgs & {
  canFocus?: (cell: FocusCell) => boolean;
}): FocusCell | undefined => {
  const step = (from: FocusCell) => {
    // After crossing sides the index lists swap roles.
    const sameSide = from.side === current.side;
    return stepFocusCell({
      key,
      current: from,
      actionableIndexes: sameSide ? actionableIndexes : crossSideIndexes,
      crossSideIndexes: sameSide ? crossSideIndexes : actionableIndexes,
      rowCount,
    });
  };

  let candidate = step(current);
  // Bounded by the size of the grid, so an entirely empty chain terminates.
  const columns = Math.max(
    1,
    actionableIndexes.length + crossSideIndexes.length,
  );
  const limit = rowCount * columns * 2 + 2;

  for (let guard = 0; guard < limit && candidate; guard += 1) {
    if (canFocus(candidate)) {
      return candidate;
    }
    const following = step(candidate);
    // The direction ran out of room, so report no movement rather than parking
    // focus on a cell with nothing to activate.
    if (!following || sameCell(following, candidate)) {
      return undefined;
    }
    candidate = following;
  }

  return undefined;
};
